import { AsyncPipe, PercentPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  ProtocolForm,
  ProtocolSectionComponent,
} from '@pages/investigator/components/protocol-section/protocol-section.component';

import { VISIT_COST_OPTION } from '@features/patient-group-dropdown/constants/visit-cost-option.const';
import { WorkflowPanelComponent } from '@features/workflow-panel/workflow-panel.component';

import { PatientGroupsQuery } from '@models/patient-group/patient-groups.query';
import { refetchListProtocolDiscontinuationRates } from '@models/patient-protocol/api/patient-protocol-discontinuation-rate.api';
import { injectPatientGroups } from '@models/patient-protocol/patient-protocol.api';
import { PatientProtocolService } from '@models/patient-protocol/patient-protocol.service';
import { SiteCostType } from '@models/sites/site-cost.enum';
import { injectTrialSettingByName } from '@models/trial-settings/api/trial-settings.api';
import { DiscontinuationRateMethodology } from '@models/trial-settings/enums/discontinuation-setting.enum';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ButtonToggleItem } from '@shared/components/button-toggle-group/button-toggle-item.model';
import { EditableListDropdownItem } from '@shared/components/editable-list-dropdown/editable-list-dropdown-item.model';
import { InfoSectionComponent } from '@shared/components/info-section/info-section.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { SkeletonLoaderDirective } from '@shared/directives/skeleton-loader.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  EventType,
  listPatientGroupsQuery,
  PatientGroupType,
  PermissionType,
  TrialSettingKey,
  WorkflowStep,
} from '@shared/services/gql.service';
import { injectFlags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventQuery } from '@shared/store/event/event.query';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { Option } from '@shared/types/components.type';
import { Maybe } from '@shared/utils/utils';

import { ProtocolEntryTableComponent } from './components/protocol-entry-table/protocol-entry-table.component';
import {
  ProtocolVersionModalComponent,
  ProtocolVersionModalData,
} from './components/protocol-version-modal/protocol-version-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    WorkflowPanelComponent,
    ProtocolSectionComponent,
    ButtonComponent,
    TooltipDirective,
    ProtocolEntryTableComponent,
    AsyncPipe,
    InfoSectionComponent,
    PercentPipe,
    SkeletonLoaderDirective,
  ],
  selector: 'aux-patient-protocol-edit',
  styleUrls: ['./patient-protocol-edit.component.scss'],
  templateUrl: './patient-protocol-edit.component.html',
})
export class PatientProtocolEditComponent {
  eventQuery = inject(EventQuery);

  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private patientGroupQuery = inject(PatientGroupsQuery);
  private patientProtocolService = inject(PatientProtocolService);
  private workflowQuery = inject(WorkflowQuery);

  discontinuationRateSetting = injectTrialSettingByName(
    TrialSettingKey.TRIAL_SETTING_DISCONTINUATION_RATE_METHODOLOGY
  );
  flags = injectFlags({
    investigatorDiscontinuationRate: 'investigator_discontinuation_rate',
    visitCosts: 'visit_costs',
  });
  patientGroups = injectPatientGroups({
    patientGroupTypes: [PatientGroupType.PATIENT_GROUP_STANDARD],
  });

  editModeGrid = signal(false);
  nullableProtocolForm = signal<FormGroup | null>(null);
  selectedCostType = signal(SiteCostType.PATIENT_VISITS);
  selectedPatientGroup = signal<null | string>(null);
  selectedProtocolVersion = signal<null | string>(null);
  userHasUpdateProtocolEntryPermission = signal(false);

  protocolEntryTable = viewChild<ProtocolEntryTableComponent>('protocolEntryTable');
  protocolSection = viewChild(ProtocolSectionComponent);

  hasChanges = computed(() => {
    return !!(
      this.protocolEntryTable()?.hasChanges() ||
      this.protocolSection()?.discontinuationRateInput()?.hasChanges()
    );
  });
  hasOverallDiscontinuationRate = computed(
    () =>
      this.discontinuationRateSetting.setting()?.setting_value ===
      DiscontinuationRateMethodology.PROTOCOL_LEVEL
  );
  noPermissionTooltip = computed(() =>
    !this.userHasUpdateProtocolEntryPermission()
      ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
      : ''
  );

  isPatientsFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER
  );

  patientTrackerLockedTooltip = computed(() =>
    this.isPatientsFinalized() ? MessagesConstants.PATIENT_TRACKER_CLOSED : ''
  );

  iCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  isClosingPanelEnabled = this.launchDarklyService.$select(
    (flags) => flags.closing_checklist_toolbar
  );
  isProtocolVersionLoading$ = new BehaviorSubject(true);
  isQuarterCloseEnabled = this.workflowQuery.isWorkflowAvailable;
  patientOptions: ButtonToggleItem[] = [];
  protocolForm!: FormGroup;
  protocolVersionOptions: (Option & { effective_date?: Maybe<string> })[] = [];
  userHasLockInvestigatorDataPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_CHECKLIST_PATIENT_DATA],
  });
  workflowName = WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_PATIENT_TRACKER;

  cancelEditMode = (): void => {
    this.editModeGrid.set(false);
    this.protocolEntryTable()?.cancelChanges();
  };

  async canDeactivate(): Promise<boolean> {
    if (this.hasChanges()) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  constructor() {
    this.setUserPermissions();

    this.mainQuery
      .select('trialKey')
      .pipe(
        takeUntilDestroyed(),
        switchMap(() => {
          this.isProtocolVersionLoading$.next(true);

          return this.patientProtocolService.getPatientProtocolVersions();
        })
      )
      .subscribe((versions) => {
        this.isProtocolVersionLoading$.next(false);
        this.protocolVersionOptions = versions.map(({ effective_date, id, name }) => ({
          effective_date,
          label: name,
          value: id,
        }));
      });

    combineLatest([this.isProtocolVersionLoading$, this.patientGroupQuery.selectAll()])
      .pipe(takeUntilDestroyed())
      .subscribe(([isProtocolVersionLoading, patientGroupList]) => {
        if (!isProtocolVersionLoading && patientGroupList.length) {
          this.setDefaultProtocolForm();
        }
      });

    effect(() => {
      const siteBudgetCostType = this.selectedCostType();
      const patientGroup = this.getInitialPatientGroup(this.patientGroups.data() || []);
      const patientProtocolForm = this.nullableProtocolForm();

      setTimeout(() => {
        patientProtocolForm?.patchValue({
          patientGroup,
          siteBudgetCostType,
        });
      }, 0);
    });
  }

  createProtocol = async (isAmendment?: boolean) => {
    const response = await firstValueFrom(
      this.overlayService.openPopup<
        ProtocolVersionModalData,
        { version: Option },
        ProtocolVersionModalComponent
      >({
        content: ProtocolVersionModalComponent,
        data: {
          modalView: isAmendment ? 'amendment' : 'create',
          patientGroup: this.getPatientGroupForAmendmentModal(),
          protocolVersionOptions: this.protocolVersionOptions,
        },
        settings: {
          header: isAmendment ? 'Create Protocol Amendment' : 'Create Protocol',
          primaryButton: {
            action: (instance) => instance?.onSave(),
            disabled: (instance) => !!instance?.isInvalid(),
            label: 'Create',
          },
        },
      }).afterClosed$
    );

    if (response.data?.version) {
      this.protocolVersionOptions = [response.data.version, ...this.protocolVersionOptions];

      this.protocolForm.controls.protocolVersion.setValue(response.data.version.value);

      const successMessage = isAmendment ? 'Amendment Created' : 'Protocol Created';

      runInInjectionContext(this.injector, () => {
        void refetchListProtocolDiscontinuationRates();
      });

      this.overlayService.showFieldTooltip('protocolVersion', successMessage);
    }
  };

  async deleteProtocolVersion(version: EditableListDropdownItem) {
    if (await this.deleteProtocolVersionConfirmation()) {
      const success = await this.patientProtocolService.removePatientProtocolVersion(
        version.value,
        version.name
      );

      const isSelectedVersionDeleted = this.protocolForm.value.protocolVersion === version.value;

      if (success) {
        this.protocolVersionOptions = this.protocolVersionOptions.filter(
          ({ value }) => value !== version.value
        );

        if (isSelectedVersionDeleted) {
          this.protocolForm.controls.protocolVersion.setValue(
            this.protocolVersionOptions[0]?.value || null
          );
        }
      }
    }
  }

  private async deleteProtocolVersionConfirmation(): Promise<boolean | undefined> {
    const modalEvent = this.overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        keywordToExecuteAction: 'Delete Protocol Version',
        message:
          'This will permanently delete the Protocol Version and all site budgets if applicable. This action cannot be undone and will remove all associated investigator data used for actuals.',
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Delete Protocol Version?',
        primaryButton: {
          label: 'Delete Protocol Version',
          variant: 'negative',
        },
      },
    }).afterClosed$;

    const resp = await firstValueFrom(modalEvent);

    return !!resp?.data;
  }

  editGrid = (): void => {
    this.editModeGrid.set(true);

    setTimeout(() => {
      this.protocolEntryTable()?.gridAPI()?.startEditingCell({
        colKey: 'patient_protocol_name',
        rowIndex: 0,
      });
    }, 0);
  };

  async editProtocolVersion(version: EditableListDropdownItem) {
    const effectiveDateTemplate = 'Effective Date: ';

    const { data } = await firstValueFrom(
      this.overlayService.openPopup<
        ProtocolVersionModalData,
        {
          effective_date: string;
          success: boolean;
          updatedVersionName: string;
        },
        ProtocolVersionModalComponent
      >({
        content: ProtocolVersionModalComponent,
        data: {
          modalView: 'edit',
          patientGroup: this.getPatientGroupForAmendmentModal(),
          protocolVersion: {
            effective_date: version.bottomText?.replace(effectiveDateTemplate, ''),
            id: version.value,
            name: version.name,
          },
          protocolVersionOptions: this.protocolVersionOptions,
        },
        settings: {
          header: 'Edit Protocol Version',
          primaryButton: {
            action: (instance) => instance?.onEdit(),
            disabled: (instance) => !!instance?.isInvalid(),
            label: 'Save',
          },
        },
      }).afterClosed$
    );

    if (data?.success && data?.updatedVersionName) {
      this.protocolVersionOptions = this.protocolVersionOptions.map((v) =>
        v.value === version.value
          ? {
              ...v,
              effective_date: data.effective_date,
              label: data?.updatedVersionName,
            }
          : v
      );

      this.cdr.markForCheck();
    }
  }

  filterCostOption(options: Option[]): Option[] {
    return options.filter((option) => option.value !== SiteCostType.SITE_INVOICEABLES);
  }

  private getInitialPatientGroup(patientGroups: listPatientGroupsQuery[]): null | string {
    if (this.selectedCostType() !== SiteCostType.PATIENT_VISITS) {
      return ' ';
    }

    const isVisitCostEnabled = this.flags().visitCosts;

    if (patientGroups.length) {
      return patientGroups[0].id;
    }

    return isVisitCostEnabled ? VISIT_COST_OPTION.value : null;
  }

  getPatientGroupForAmendmentModal(): Maybe<string> {
    return this.selectedPatientGroup() === VISIT_COST_OPTION.value
      ? undefined
      : this.selectedPatientGroup();
  }

  onChangePatientGroup(form: ProtocolForm) {
    this.protocolEntryTable()?.cancelChanges();
    this.selectedPatientGroup.set(form.patientGroup);
    this.selectedProtocolVersion.set(form.protocolVersion);
    this.selectedCostType.set(form.siteBudgetCostType);
  }

  onProtocolFormReady(form: FormGroup) {
    this.protocolForm = form;
    this.nullableProtocolForm.set(form);
    this.protocolForm.controls.siteBudgetCostType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.protocolEntryTable()?.gridAPI()?.sizeColumnsToFit();
      });

    this.setDefaultProtocolForm();
  }

  onSaveAll = async () => {
    if (this.protocolSection()?.discontinuationRateInput()?.isSaveDisabled()) return;

    await this.protocolSection()?.discontinuationRateInput()?.onSave();

    await this.protocolEntryTable()?.saveAll(() => {
      this.editModeGrid.set(false);
    });
  };

  private setDefaultProtocolForm() {
    setTimeout(() => {
      this.nullableProtocolForm()?.patchValue({
        protocolVersion: this.protocolVersionOptions[0]?.value || null,
        siteBudgetCostType: SiteCostType.PATIENT_VISITS,
      });
    }, 0);
  }

  private setUserPermissions(): void {
    combineLatest([
      this.authService.isAuthorized$({
        permissions: [PermissionType.PERMISSION_UPDATE_PROTOCOL_ENTRY],
        sysAdminsOnly: false,
      }),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([userHasUpdateProtocolEntryPermission]) => {
        this.userHasUpdateProtocolEntryPermission.set(userHasUpdateProtocolEntryPermission);
      });
  }
}
